import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import 'react-toastify/dist/ReactToastify.css';
// import 'react-table/react-table.css';
import 'assets/styles/datatables.min.css';
import 'react-quill/dist/quill.snow.css'; // ES6
import './assets/styles/index.scss';
import './assets/styles/mobile.scss';
import './assets/styles/showPreview.scss';

import Layout from 'shared/hoc/layout/component/layout.component';
import AuthService from 'shared/services/auth.service';
import Spinner from 'shared/components/spinner/spinner';
import NotFound from 'shared/components/404/404';
import withRouter from 'shared/hoc/withRouter';
import RouteManager from 'shared/hoc/permission/routeManager';
import { logUserPermissions } from 'shared/util/permission';
import { PermissionList } from 'shared/interface/enum';
import AiAssistant from 'features/ai/container/aiAssistant';

// import containers, for which lazy loading is not needed, as they will be rendered for all users
import Login from 'features/login/container/login.container';

const Users = lazy(() => import('features/users/container/users'));

// const EarlyAccess = lazy(() => import('features/earlyAccess/container/earlyAccess'));

const Creators = lazy(() => import('features/creator/container/creator'));
const CreatorExportRoute = lazy(() => import('features/creator/container/creatorExportRoute'));

const Dashboard = lazy(() => import('features/dashboard/container/dashboard'));

const DashboardDayWise = lazy(() => import('features/dashboard/container/dashboardDaywise'));

const DashboardExclusive = lazy(() => import('features/dashboard/container/dashboardExclusive'));
const DashboardMonthly = lazy(() => import('features/dashboard/container/dashboardMonthly'));

const CreatorsDetailComponent = lazy(() => import('features/creator/container/creatorDetailComponent'));

const Shop = lazy(() => import('features/shops/container/shops'));

const Profile = lazy(() => import('features/profile/container/profile'));

const ApplicationPageBuilder = lazy(() => import('features/applicationPageBuilder/container/ApplicationPageBuilder'));

const ApplicantList = lazy(() => import('features/applicantList/container/applicantList'));

const SubscriptionPlans = lazy(() => import('features/subscriptionPlans/container/subscriptionPlans'));

// const ReferURL = lazy(() => import('features/referUrl/container/referURL'));

// const Explore = lazy(() => import('features/explore/container/explore'));

// const SupportChat = lazy(() => import('features/supportChat/container/supportChat'));

const Roles = lazy(() => import('features/roles/container/roles'));

// const BanUnBanUsers = lazy(() => import('features/banUnBanUser/container/banUnBanUser'));

const AvailableNumbers = lazy(() => import('features/availableNumbers/container/availableNumbers'));

const AppConfig = lazy(() => import('features/appConfig/container/appConfig'));

const Payout = lazy(() => import('features/payout/container/payout'));

const CreatorTransaction = lazy(() => import('features/transaction/container/creatorTransaction'));

const FanTransaction = lazy(() => import('features/transaction/container/fanTransaction'));

const AddBonus = lazy(() => import('features/addBonus/container/addBonus'));
const SmsPackage = lazy(() => import('features/smsPackagePlan/container/smsPackage'));

const BusinessManagementMaster = lazy(() => import('features/businessManagement/container/master'));
const BusinessManagementCreators = lazy(() => import('features/businessManagement/container/creators'));
const BusinessManagementTwilio = lazy(() => import('features/businessManagement/container/twilio'));
const BusinessManagementTwilioDetail = lazy(() => import('features/businessManagement/container/twilioDetail'));

const PaymentUserValues = lazy(() => import('features/paymentUserValue/container/paymentUserValue'));
const PaymentList = lazy(() => import('features/payment/container/paymentList'));
const PaymentDetail = lazy(() => import('features/payment/container/paymentDetail'));
const FanUsers = lazy(() => import('features/fanUsers/container/fanUsers'));
const FanUserDetail = lazy(() => import('features/fanUsers/container/fanUserDetail'));
const Subscriptions = lazy(() => import('features/subscriptions/container/subscriptions'));
const SubscriptionDetail = lazy(() => import('features/subscriptions/container/subscriptionDetail'));
const WebhookLogs = lazy(() => import('features/webhookLogs/container/webhookLogs'));
const WebhookLogDetail = lazy(() => import('features/webhookLogs/container/webhookLogDetail'));
const Conversations = lazy(() => import('features/conversations/container/conversations'));
const CreditNotesList = lazy(() => import('features/creditNotes/container/creditNotesList'));
const Chat = lazy(() => import('features/chat/container/chat'));

const App: React.FC = () => {
	const isLoggedIn = AuthService.checkLogin();

	useEffect(() => {
		if (isLoggedIn) {
			logUserPermissions();
		}
	}, [isLoggedIn]);

	if (isLoggedIn) {
		return (
			<Layout>
				<Suspense fallback={<Spinner />}>
					<Routes>
						<Route
							path="/dashboard/aggregated"
							element={
								<RouteManager permission={[PermissionList.DashboardView]} component={<Dashboard />} />
							}
						/>
						<Route
							path="/dashboard/daywise"
							element={
								<RouteManager
									permission={[PermissionList.DashboardView]}
									component={<DashboardDayWise />}
								/>
							}
						/>
						<Route
							path="/dashboard/monthly"
							element={
								<RouteManager
									permission={[PermissionList.DashboardView]}
									component={<DashboardMonthly />}
								/>
							}
						/>
						<Route
							path="/chat"
							element={<RouteManager permission={[PermissionList.ChatExport]} component={<Chat />} />}
						/>
						<Route
							path="/dashboard/exclusive"
							element={
								<RouteManager
									permission={[PermissionList.DashboardView]}
									component={<DashboardExclusive />}
								/>
							}
						/>
						<Route
							path="/dashboard"
							element={
								<RouteManager permission={[PermissionList.DashboardView]} component={<Dashboard />} />
							}
						/>
						<Route path="/creators" element={<Creators />} />
						<Route
							path="/creator/export"
							element={
								<RouteManager
									permission={[PermissionList.CreatorExport]}
									component={<CreatorExportRoute />}
								/>
							}
						/>

						<Route path="/messaging-config/bm" element={<BusinessManagementMaster />} />
						<Route path="/messaging-config/bm/:bmId" element={<BusinessManagementCreators />} />
						<Route path="/messaging-config/creators" element={<BusinessManagementCreators />} />
						<Route path="/messaging-config/twilio" element={<BusinessManagementTwilio />} />
						<Route path="/messaging-config/twilio/:id" element={<BusinessManagementTwilioDetail />} />

						<Route
							path="/profile"
							element={<RouteManager permission={[PermissionList.AccountMe]} component={<Profile />} />}
						/>
						<Route
							path="/creator/:id/:appId"
							element={
								<RouteManager
									permission={[PermissionList.ApplicationFormView]}
									component={<ApplicationPageBuilder />}
								/>
							}
						/>
						<Route
							path="/creator/:id/:type/:pollId/:appId"
							element={
								<RouteManager
									permission={[PermissionList.PollApplicantList]}
									component={<ApplicantList />}
								/>
							}
						/>
						{/* <Route
							path="/refer-url"
							element={
								<RouteManager permission={[PermissionList.ReferUrlList]} component={<ReferURL />} />
							}
						/> */}
						{/* <Route
							path="/explore"
							element={<RouteManager permission={[PermissionList.ExploreAll]} component={<Explore />} />}
						/> */}
						<Route
							path="/shop"
							element={
								<RouteManager permission={[PermissionList.EnergyPackageList]} component={<Shop />} />
							}
						/>
						<Route
							path="/users"
							element={<RouteManager permission={[PermissionList.UserList]} component={<Users />} />}
						/>
						{/* <Route
							path="/early-access"
							element={
								<RouteManager
									permission={[PermissionList.EarlyAccessGet]}
									component={<EarlyAccess />}
								/>
							}
						/> */}
						<Route
							path="subscriptionPlans"
							element={
								<RouteManager
									permission={[PermissionList.CreatorConfigPlanList]}
									component={<SubscriptionPlans />}
								/>
							}
						/>
						{/* <Route
							path="support-chat"
							element={
								<RouteManager
									permission={[PermissionList.ChatModeratorGetCreatorAccess]}
									component={<SupportChat />}
								/>
							}
						/> */}
						<Route
							path="/roles"
							element={<RouteManager permission={[PermissionList.RolesList]} component={<Roles />} />}
						/>
						<Route
							path="/creator/:id"
							element={
								<RouteManager
									permission={[PermissionList.CreatorView]}
									component={<CreatorsDetailComponent />}
								/>
							}
						/>
						{/* <Route
							path="/unban-users"
							element={
								<RouteManager
									permission={[PermissionList.BanAndUnBanUserList]}
									component={<BanUnBanUsers />}
								/>
							}
						/> */}
						<Route
							path="/payout"
							element={<RouteManager permission={[PermissionList.PayoutsGet]} component={<Payout />} />}
						/>
						<Route
							path="/transaction"
							element={
								<RouteManager
									permission={[PermissionList.CreatorPayment]}
									component={<CreatorTransaction />}
								/>
							}
						/>
						<Route
							path="/fan-transaction"
							element={
								<RouteManager
									anyOnePermission={[
										PermissionList.FanPaymentEnergyList,
										PermissionList.FanPaymentVipList,
										PermissionList.FanPaymentDirectPurchaseList,
										PermissionList.FanPaymentSuperSmsList,
										PermissionList.FanPaymentTipList,
										PermissionList.FanPaymentAllList,
										PermissionList.FanPaymentSignUpList
									]}
									component={<FanTransaction />}
								/>
							}
						/>
						<Route
							path="/add-bonus"
							element={<RouteManager permission={[PermissionList.BonusList]} component={<AddBonus />} />}
						/>
						<Route
							path="/sms-package"
							element={
								<RouteManager permission={[PermissionList.SmsPackageList]} component={<SmsPackage />} />
							}
						/>
						<Route
							path="/subscriptions"
							element={
								<RouteManager permission={[PermissionList.FullAccess]} component={<Subscriptions />} />
							}
						/>
						<Route
							path="/subscriptions/:id"
							element={
								<RouteManager
									permission={[PermissionList.FullAccess]}
									component={<SubscriptionDetail />}
								/>
							}
						/>
						<Route
							path="/credit-notes"
							element={
								<RouteManager
									permission={[PermissionList.CreditNotesList]}
									component={<CreditNotesList />}
								/>
							}
						/>
						<Route
							path="/webhook-logs"
							element={
								<RouteManager permission={[PermissionList.FullAccess]} component={<WebhookLogs />} />
							}
						/>
						<Route
							path="/webhook-logs/:id"
							element={
								<RouteManager
									permission={[PermissionList.FullAccess]}
									component={<WebhookLogDetail />}
								/>
							}
						/>
						<Route path="/available-numbers" element={<AvailableNumbers />} />
						<Route path="/payment-user-values" element={<PaymentUserValues />} />
						<Route
							path="/payment-list/:referenceId"
							element={
								<RouteManager permission={[PermissionList.FullAccess]} component={<PaymentDetail />} />
							}
						/>
						<Route
							path="/payment-list"
							element={
								<RouteManager permission={[PermissionList.FullAccess]} component={<PaymentList />} />
							}
						/>
						<Route
							path="/fan-users/:userId"
							element={
								<RouteManager permission={[PermissionList.FanUserList]} component={<FanUserDetail />} />
							}
						/>
						<Route
							path="/fan-users"
							element={
								<RouteManager permission={[PermissionList.FanUserList]} component={<FanUsers />} />
							}
						/>
						<Route
							path="/conversations"
							element={
								<RouteManager
									anyOnePermission={[PermissionList.TelegramGroupList, PermissionList.FullAccess]}
									component={<Conversations />}
								/>
							}
						/>
						<Route
							path="/ai-assistant"
							element={
								<RouteManager
									anyOnePermission={[
										PermissionList.AiConversationList,
										PermissionList.AiConversationGet,
										PermissionList.AiConversationAsk,
										PermissionList.FullAccess
									]}
									component={<AiAssistant />}
								/>
							}
						/>
						<Route path="/app-config" element={<AppConfig />} />
						<Route path="/404" element={<NotFound />} />
						<Route path="*" element={<Navigate replace to="/creators" />} />
					</Routes>
				</Suspense>
			</Layout>
		);
	} else {
		// lazy loading not needed for login, forget password, reset password APIs
		return (
			<Routes>
				<Route path="/login" element={<Login />} />
				<Route path="*" element={<Navigate replace to="/login" />} />
			</Routes>
		);
	}
};

export default withRouter(App);
